<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  consumed: {
    type: Number,
    required: true,
  },
  allowed: {
    type: Number,
    default: null,
  },
  totalCount: {
    type: Number,
    default: null,
  },
  remaining: {
    type: Number,
    default: null,
  },
  overLimit: {
    type: Boolean,
    default: false,
  },
});
const { t } = useI18n();

const maximum = computed(() => props.allowed ?? props.totalCount ?? 0);
const ariaMaximum = computed(() => Math.max(maximum.value, props.consumed, 1));
const usageLabel = computed(() => `${props.consumed} / ${maximum.value}`);
const remainingCount = computed(
  () => props.remaining ?? Math.max(maximum.value - props.consumed, 0)
);
const ariaValueText = computed(() => {
  const values = {
    consumed: props.consumed,
    allowed: maximum.value,
    remaining: remainingCount.value,
  };
  if (props.overLimit) {
    return t('BILLING_SETTINGS.USAGE.METER_DESCRIPTION_OVER_LIMIT', values);
  }
  return t('BILLING_SETTINGS.USAGE.METER_DESCRIPTION', values);
});
const percent = computed(() =>
  Math.round((props.consumed / ariaMaximum.value) * 100)
);

const colorClass = computed(() => {
  if (props.overLimit) return 'accent-n-ruby-9';
  if (percent.value < 50) {
    return 'accent-n-teal-9';
  }
  if (percent.value < 80) {
    return 'accent-n-amber-9';
  }
  return 'accent-n-ruby-9';
});
</script>

<template>
  <div class="grid gap-2">
    <div
      class="flex items-center justify-between gap-5 text-xs text-n-slate-10"
    >
      <div class="font-medium">
        {{ title }}
      </div>
      <div class="tabular-nums">{{ usageLabel }}</div>
    </div>
    <progress
      role="progressbar"
      class="h-2 w-full overflow-hidden rounded-full bg-n-slate-4"
      :class="colorClass"
      :max="ariaMaximum"
      :value="Math.min(consumed, ariaMaximum)"
      aria-valuemin="0"
      :aria-valuemax="maximum"
      :aria-valuenow="consumed"
      :aria-valuetext="ariaValueText"
      :aria-label="title"
    />
    <div
      class="flex items-center justify-between gap-4 text-xs text-n-slate-10"
    >
      <span>
        {{ $t('BILLING_SETTINGS.USAGE.REMAINING') }}
      </span>
      <span class="tabular-nums">
        {{ remainingCount }}
      </span>
    </div>
    <p v-if="overLimit" class="text-xs text-n-ruby-11">
      {{ $t('BILLING_SETTINGS.USAGE.OVER_LIMIT') }}
    </p>
  </div>
</template>
